import { useLingui } from '@lingui/react/macro'
import { useNavigate } from '@tanstack/react-router'
import Steps, { type StepDef } from './steps'
import { useAppStore } from '@/lib/app-store'
import { neededProfileSteps, type ProfileStep, type ProfileUser } from 'shared/applicant-form'
import { flowViewTransition } from '@/lib/utils'

export type ProfileSubStep = ProfileStep

type StepsUser = ProfileUser & {
  type?: 'company' | 'applicant'
  testPassed?: boolean | null
  testPassedValues?: boolean | null
  testPassedPersonality?: boolean | null
  isEmployee?: boolean | null
  peopleInCharge?: boolean | null
}

/** Stage the user is currently at, so the timeline can highlight the right segment. */
export type RegistrationStage =
  | { kind: 'intro' }
  | { kind: 'people' }
  | { kind: 'test'; type: 'competences' | 'values' | 'personality' }
  | { kind: 'profile'; sub: ProfileSubStep }

function stageKey(stage: RegistrationStage): string {
  switch (stage.kind) {
    case 'intro':
      return 'intro'
    case 'people':
      return 'people'
    case 'test':
      return stage.type
    case 'profile':
      return stage.sub
  }
}

export function RegistrationSteps(props: {
  user: StepsUser | null | undefined
  stage: RegistrationStage
  /** When on the profile wizard, lets the user jump between sub-steps via the pills. */
  onProfileStepClick?: (sub: ProfileSubStep) => void
  /** Explicit profile sub-step list (the wizard snapshots it so the indicator stays stable mid-flow). */
  profileSteps?: ProfileStep[]
}) {
  const { t } = useLingui()
  const navigate = useNavigate()
  const peopleStepShown = useAppStore((state) => state.peopleStepShown)

  const user = props.user
  const allTestsPassed = !!user?.testPassed && !!user?.testPassedValues && !!user?.testPassedPersonality
  const inWizard = !!props.onProfileStepClick
  // Inside the wizard the profile pills use the default positional rule (past clickable only, so the
  // user can't skip ahead). Outside: unlocked once all tests pass so the user can jump into the wizard.
  const profilePillNavigable = !inWizard && allTestsPassed ? true : undefined
  const isEmployee = !!user?.isEmployee

  // The "people in charge" question is its own step right after the intro, shown only for applicants
  // who still need to answer it. The store latch keeps it visible (as completed) once shown, so the
  // timeline doesn't shrink after they answer (see app-store.peopleStepShown).
  const showPeople =
    props.stage.kind === 'people' ||
    peopleStepShown ||
    (!!user && user.type !== 'company' && user.peopleInCharge == null)

  // Employees never fill the profile (the company supplies it). Everyone else shows only the steps
  // that still have missing fields — fully-prefilled steps (API/SDK) drop off entirely.
  const profile: ProfileStep[] = props.profileSteps ?? (isEmployee || !user ? [] : neededProfileSteps(user))
  const profileTitle: Record<ProfileStep, string> = { about: t`About you`, location: t`Location`, career: t`Career` }

  const steps: StepDef[] = [
    { key: 'intro', title: t`Introduction`, navigable: true },
    ...(showPeople ? [{ key: 'people', title: t`Experience`, navigable: false }] : []),
    { key: 'competences', title: t`Competencies`, navigable: !!user?.testPassed || undefined },
    { key: 'values', title: t`Values`, navigable: !!user?.testPassedValues || undefined },
    { key: 'personality', title: t`Personality`, navigable: !!user?.testPassedPersonality || undefined },
    ...profile.map((sub) => ({ key: sub, title: profileTitle[sub], navigable: profilePillNavigable })),
  ]

  const currentKey = stageKey(props.stage)
  const foundIndex = steps.findIndex((step) => step.key === currentKey)
  const stepIndex = foundIndex === -1 ? 0 : foundIndex

  function onStepClick(index: number) {
    const key = steps[index]?.key
    // viewTransition morphs the box like the step-to-step navigations; intro has no box so skip it.
    if (key === 'intro') navigate({ to: '/test' })
    else if (key === 'competences' && user?.testPassed)
      navigate({ to: '/test/competences', viewTransition: flowViewTransition() })
    else if (key === 'values' && user?.testPassedValues)
      navigate({ to: '/test/values', viewTransition: flowViewTransition() })
    else if (key === 'personality' && user?.testPassedPersonality)
      navigate({ to: '/test/personality', viewTransition: flowViewTransition() })
    else if (key === 'about' || key === 'location' || key === 'career') {
      if (props.onProfileStepClick) props.onProfileStepClick(key)
      else if (allTestsPassed)
        navigate({ to: '/register/applicant', search: { step: key }, viewTransition: flowViewTransition() })
    }
  }

  return <Steps steps={steps} stepIndex={stepIndex} onStepClick={onStepClick} />
}
